import { useAtomValue, useSetAtom } from 'jotai';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldRow, NumberInput, Slider } from '@/core/ui';
import { UNIT_SYSTEMS, unitsAtom } from '@/features/units';
import { displayText, GLIDE_RATIO_RANGE, HEIGHT_INPUT_STEPS, type MeasuredSetting } from '../model/settings';
import { commitSettingsAtom } from '../state/actions';
import { settingsInputAtom } from '../state/atoms';

type InputProps = Omit<ComponentProps<typeof NumberInput>, 'value' | 'onCommit'>;

/** A height setting, typed and shown in the current unit system. */
function MeasuredField({ setting, ...props }: { setting: MeasuredSetting } & InputProps) {
  const { t } = useTranslation('glide');
  const units = useAtomValue(unitsAtom);
  const input = useAtomValue(settingsInputAtom);
  const commit = useSetAtom(commitSettingsAtom);
  return (
    <FieldRow label={t(`settings.${setting}`, { unit: UNIT_SYSTEMS[units].height.symbol })}>
      <NumberInput
        value={displayText(input, setting, units)}
        onCommit={(text) => commit({ [setting]: { text, system: units } })}
        {...props}
      />
    </FieldRow>
  );
}

export function GlideRatioField() {
  const { t } = useTranslation('glide');
  const { glideRatio } = useAtomValue(settingsInputAtom);
  const commit = useSetAtom(commitSettingsAtom);
  return (
    <Slider
      label={t('settings.glideRatio')}
      decreaseLabel={t('settings.decreaseGlideRatio')}
      increaseLabel={t('settings.increaseGlideRatio')}
      {...GLIDE_RATIO_RANGE}
      value={glideRatio}
      onCommit={(value) => commit({ glideRatio: value })}
      className="gap-1.5 pt-0 md:gap-1"
    />
  );
}

export function ClearanceField() {
  const units = useAtomValue(unitsAtom);
  return <MeasuredField setting="clearance" min={0} step={HEIGHT_INPUT_STEPS[units].clearance} />;
}
